/**
 * SectionsEditor — drag-orderable list of campaign sections.
 * Uses @dnd-kit/sortable (already in package.json).
 *
 * Each section is a { id, type, enabled, config } object. This component
 * doesn't know how to RENDER a section — that's SpotlightPage's job. It
 * only edits the config via type-specific mini-forms.
 */
import { useState } from "react";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
    GripVertical, Trash2, Plus, Eye, EyeOff, Video, Award, Users, HelpCircle,
    Image as ImageIcon, ArrowRight, Code2, FileText,
} from "lucide-react";

export const SECTION_TYPES = [
    { type: "video",      label: "Video",         icon: Video,        default: () => ({ url: "", caption: "" }) },
    { type: "benefits",   label: "Benefits list", icon: Award,        default: () => ({ title: "Why join us", items: [{ label: "", description: "" }] }) },
    { type: "team",       label: "Team photos",   icon: Users,        default: () => ({ title: "Meet the team", members: [{ name: "", role: "", avatar_url: "" }] }) },
    { type: "faq",        label: "FAQ",           icon: HelpCircle,   default: () => ({ title: "Common questions", items: [{ q: "", a: "" }] }) },
    { type: "gallery",    label: "Photo gallery", icon: ImageIcon,    default: () => ({ title: "Life at ...", images: [""] }) },
    { type: "cta",        label: "Call to action",icon: ArrowRight,   default: () => ({ headline: "", label: "Apply", url: "", note: "" }) },
    { type: "rich_text",  label: "Rich text",     icon: FileText,     default: () => ({ title: "", body_markdown: "" }) },
    { type: "html",       label: "Custom HTML",   icon: Code2,        default: () => ({ html: "<p>Custom HTML here</p>" }) },
];

export default function SectionsEditor({ sections, onChange }) {
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

    const handleDragEnd = ({ active, over }) => {
        if (!over || active.id === over.id) return;
        const oldIndex = sections.findIndex((s) => s.id === active.id);
        const newIndex = sections.findIndex((s) => s.id === over.id);
        if (oldIndex < 0 || newIndex < 0) return;
        onChange(arrayMove(sections, oldIndex, newIndex));
    };

    const add = (type) => {
        const def = SECTION_TYPES.find((s) => s.type === type);
        if (!def) return;
        onChange([
            ...sections,
            { id: `${type}-${Math.random().toString(36).slice(2, 8)}`, type, enabled: true, config: def.default() },
        ]);
    };
    const update = (id, patch) => onChange(sections.map((s) => (s.id === id ? { ...s, ...patch } : s)));
    const remove = (id) => onChange(sections.filter((s) => s.id !== id));

    return (
        <div className="space-y-3" data-testid="sections-editor">
            <div className="border border-dashed border-border p-3 bg-secondary/20 text-xs text-muted-foreground">
                Sections render below the hero, in the order shown. Drag by the grip handle to reorder. Toggle the eye icon to hide without deleting.
            </div>

            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={sections.map((s) => s.id)} strategy={verticalListSortingStrategy}>
                    <ul className="space-y-2" data-testid="sections-list">
                        {sections.map((s) => (
                            <SortableSection key={s.id} section={s} onUpdate={update} onRemove={remove} />
                        ))}
                    </ul>
                </SortableContext>
            </DndContext>

            {sections.length === 0 && (
                <div className="border border-border p-6 text-center text-xs text-muted-foreground bg-white">
                    No custom sections yet. Add one below.
                </div>
            )}

            <div className="border border-border bg-white p-3">
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Add a section</div>
                <div className="flex flex-wrap gap-1.5">
                    {SECTION_TYPES.map((def) => {
                        const Icon = def.icon;
                        return (
                            <button
                                key={def.type}
                                type="button"
                                onClick={() => add(def.type)}
                                className="h-8 px-3 border border-border hover:border-ikb hover:text-ikb text-[11px] font-mono uppercase tracking-widest inline-flex items-center gap-1.5"
                                data-testid={`section-add-${def.type}`}
                            >
                                <Icon className="w-3.5 h-3.5" /> {def.label}
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

function SortableSection({ section, onUpdate, onRemove }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: section.id });
    const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 };
    const def = SECTION_TYPES.find((s) => s.type === section.type);
    const Icon = def?.icon || Plus;

    return (
        <li
            ref={setNodeRef}
            style={style}
            className={`border border-border bg-white ${section.enabled ? "" : "opacity-60"}`}
            data-testid={`section-${section.id}`}
        >
            <header className="flex items-center gap-2 px-3 py-2 border-b border-border bg-secondary/40">
                <button type="button" {...attributes} {...listeners}
                    className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground"
                    aria-label="Drag to reorder"
                    data-testid={`section-drag-${section.id}`}>
                    <GripVertical className="w-4 h-4" />
                </button>
                <Icon className="w-3.5 h-3.5 text-ikb" />
                <span className="text-[11px] font-mono uppercase tracking-widest flex-1">{def?.label || section.type}</span>
                <button type="button" onClick={() => onUpdate(section.id, { enabled: !section.enabled })}
                    className="h-7 w-7 grid place-items-center border border-border hover:border-ikb"
                    title={section.enabled ? "Hide section" : "Show section"}
                    data-testid={`section-toggle-${section.id}`}
                    aria-label={section.enabled ? "Hide section" : "Show section"}>
                    {section.enabled ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                </button>
                <button type="button" onClick={() => onRemove(section.id)}
                    className="h-7 w-7 grid place-items-center border border-border hover:border-rose-500 hover:text-rose-700"
                    title="Delete section"
                    data-testid={`section-delete-${section.id}`}
                    aria-label="Delete section">
                    <Trash2 className="w-3.5 h-3.5" />
                </button>
            </header>
            <div className="p-3 space-y-2">
                <SectionConfigForm section={section} onUpdate={(patch) => onUpdate(section.id, { config: { ...section.config, ...patch } })} />
            </div>
        </li>
    );
}

function SectionConfigForm({ section, onUpdate }) {
    const c = section.config || {};
    const setList = (key, next) => onUpdate({ [key]: next });

    if (section.type === "video") {
        return (
            <>
                <MiniLabel>Video URL (YouTube, Vimeo, or MP4)</MiniLabel>
                <Input value={c.url || ""} onChange={(e) => onUpdate({ url: e.target.value })} placeholder="https://youtube.com/watch?v=…" className="rounded-none text-xs" data-testid={`cfg-video-url-${section.id}`} />
                <MiniLabel>Caption</MiniLabel>
                <Input value={c.caption || ""} onChange={(e) => onUpdate({ caption: e.target.value })} placeholder="Optional caption" className="rounded-none text-xs" />
            </>
        );
    }

    if (section.type === "benefits") {
        return (
            <>
                <MiniLabel>Title</MiniLabel>
                <Input value={c.title || ""} onChange={(e) => onUpdate({ title: e.target.value })} className="rounded-none text-xs" />
                <MiniLabel>Items</MiniLabel>
                <RepeatingRows
                    items={c.items || []}
                    fields={[
                        { key: "label", placeholder: "Benefit label" },
                        { key: "description", placeholder: "Short description" },
                    ]}
                    onChange={(next) => setList("items", next)}
                    testId={`benefits-${section.id}`}
                />
            </>
        );
    }

    if (section.type === "team") {
        return (
            <>
                <MiniLabel>Title</MiniLabel>
                <Input value={c.title || ""} onChange={(e) => onUpdate({ title: e.target.value })} className="rounded-none text-xs" />
                <MiniLabel>Members</MiniLabel>
                <RepeatingRows
                    items={c.members || []}
                    fields={[
                        { key: "name", placeholder: "Name" },
                        { key: "role", placeholder: "Role" },
                        { key: "avatar_url", placeholder: "Avatar URL" },
                    ]}
                    onChange={(next) => setList("members", next)}
                    testId={`team-${section.id}`}
                />
            </>
        );
    }

    if (section.type === "faq") {
        return (
            <>
                <MiniLabel>Title</MiniLabel>
                <Input value={c.title || ""} onChange={(e) => onUpdate({ title: e.target.value })} className="rounded-none text-xs" />
                <MiniLabel>Questions</MiniLabel>
                <RepeatingRows
                    items={c.items || []}
                    fields={[
                        { key: "q", placeholder: "Question" },
                        { key: "a", placeholder: "Answer", multiline: true },
                    ]}
                    onChange={(next) => setList("items", next)}
                    testId={`faq-${section.id}`}
                />
            </>
        );
    }

    if (section.type === "gallery") {
        return (
            <>
                <MiniLabel>Title</MiniLabel>
                <Input value={c.title || ""} onChange={(e) => onUpdate({ title: e.target.value })} className="rounded-none text-xs" />
                <MiniLabel>Image URLs (one per row)</MiniLabel>
                <Textarea
                    value={(c.images || []).join("\n")}
                    onChange={(e) => onUpdate({ images: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })}
                    rows={4}
                    className="rounded-none text-xs font-mono"
                    placeholder="https://…/office-1.jpg
https://…/office-2.jpg"
                />
            </>
        );
    }

    if (section.type === "cta") {
        return (
            <>
                <MiniLabel>Headline</MiniLabel>
                <Input value={c.headline || ""} onChange={(e) => onUpdate({ headline: e.target.value })} className="rounded-none text-xs" />
                <div className="grid sm:grid-cols-2 gap-2">
                    <div>
                        <MiniLabel>Button label</MiniLabel>
                        <Input value={c.label || ""} onChange={(e) => onUpdate({ label: e.target.value })} className="rounded-none text-xs" />
                    </div>
                    <div>
                        <MiniLabel>Button URL</MiniLabel>
                        <Input value={c.url || ""} onChange={(e) => onUpdate({ url: e.target.value })} className="rounded-none text-xs" />
                    </div>
                </div>
                <MiniLabel>Fine print (optional)</MiniLabel>
                <Input value={c.note || ""} onChange={(e) => onUpdate({ note: e.target.value })} className="rounded-none text-xs" />
            </>
        );
    }

    if (section.type === "rich_text") {
        return (
            <>
                <MiniLabel>Title (optional)</MiniLabel>
                <Input value={c.title || ""} onChange={(e) => onUpdate({ title: e.target.value })} className="rounded-none text-xs" />
                <MiniLabel>Body (plain text; **bold**, *italic*, blank line = paragraph)</MiniLabel>
                <Textarea value={c.body_markdown || ""} onChange={(e) => onUpdate({ body_markdown: e.target.value })}
                    rows={5} className="rounded-none text-xs font-mono" />
            </>
        );
    }

    if (section.type === "html") {
        return (
            <>
                <MiniLabel className="text-amber-800">Custom HTML — Enterprise-only. Enable when your tier is upgraded.</MiniLabel>
                <Textarea value={c.html || ""} onChange={(e) => onUpdate({ html: e.target.value })}
                    rows={6} className="rounded-none text-xs font-mono" />
            </>
        );
    }

    return <div className="text-xs text-muted-foreground">Unknown section type</div>;
}

function RepeatingRows({ items, fields, onChange, testId }) {
    const add = () => onChange([...items, Object.fromEntries(fields.map((f) => [f.key, ""]))]);
    const update = (i, patch) => onChange(items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
    const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
    return (
        <div className="space-y-1.5">
            {items.map((it, i) => (
                <div key={i} className="border border-border p-1.5 flex items-start gap-1.5" data-testid={`${testId}-row-${i}`}>
                    <div className="flex-1 space-y-1">
                        {fields.map((f) => (
                            f.multiline ? (
                                <Textarea key={f.key} value={it[f.key] || ""} onChange={(e) => update(i, { [f.key]: e.target.value })}
                                    placeholder={f.placeholder} rows={2} className="rounded-none text-xs" />
                            ) : (
                                <Input key={f.key} value={it[f.key] || ""} onChange={(e) => update(i, { [f.key]: e.target.value })}
                                    placeholder={f.placeholder} className="rounded-none h-7 text-xs" />
                            )
                        ))}
                    </div>
                    <button type="button" onClick={() => remove(i)}
                        className="h-7 w-7 grid place-items-center border border-border hover:border-rose-500 hover:text-rose-700 shrink-0"
                        data-testid={`${testId}-row-del-${i}`}
                        aria-label="Remove row">
                        <Trash2 className="w-3 h-3" />
                    </button>
                </div>
            ))}
            <button type="button" onClick={add}
                className="h-7 px-2 border border-dashed border-border hover:border-ikb text-[10px] font-mono uppercase tracking-widest inline-flex items-center gap-1"
                data-testid={`${testId}-add`}>
                <Plus className="w-3 h-3" /> Add row
            </button>
        </div>
    );
}

function MiniLabel({ children, className = "" }) {
    return <div className={`text-[10px] font-mono uppercase tracking-widest text-muted-foreground ${className}`}>{children}</div>;
}
